import {
    Button,
    Dialog,
    DialogBody,
    DialogFooter,
    FormGroup,
    InputGroup,
    Intent,
    Position,
    Toaster
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import {
    GetTrackedProjectsDocument,
    useUpdateSpeculativeProjectNameMutation,
    useUpdateTrackedProjectNameMutation
} from "generated/graphql";
import { segmentTrackRenameProject } from "in_queue/analytics";
import { useCluster } from "in_queue/contexts/ClusterContext";
import { useIsProjectNameAvailable } from "in_queue/hooks/useIsProjectNameAvailable";
import {
    getProjectDisplayName,
    ProjectType,
    TrackedProject
} from "in_queue/types/projectType";
import { useState } from "react";
import { ADMIN_TYPE } from "types/roleType";
import css from "./EditableProjectTitle.module.scss";

export const EditableProjectTitle: React.FC<{ project: TrackedProject }> = ({
    project
}) => {
    const { role, teamId } = useUserDataContext();
    const canEditName = role === ADMIN_TYPE;
    const cluster = useCluster();

    const isSpeculative = project.type === ProjectType.SPECULATIVE_PROJECT;

    const [updateTrackedProjectName, updateTrackedProjectResult] =
        useUpdateTrackedProjectNameMutation();
    const [updateSpeculativeProjectName, updateSpeculativeProjectResult] =
        useUpdateSpeculativeProjectNameMutation();
    const isSaving =
        updateTrackedProjectResult.loading ||
        updateSpeculativeProjectResult.loading;

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [projectName, setProjectName] = useState("");

    const openDialog = () => {
        setProjectName(project.projectName ?? "");
        setIsDialogOpen(true);
    };

    const closeDialog = () => {
        setIsDialogOpen(false);
        setProjectName("");
    };

    const trimmedDraft = projectName.trim();
    const isNameAvailable = useIsProjectNameAvailable({
        name: projectName,
        subscriptionId: project.subscriptionId,
        currentProjectId: project.projectId
    });
    const showNameError = trimmedDraft !== "" && !isNameAvailable;

    // projectNames are required for speculative projects, but optional for tracked projects
    const isSaveDisabled =
        (isSpeculative && trimmedDraft === "") || showNameError;

    const onSave = async () => {
        if (isSaveDisabled) {
            return;
        }

        try {
            if (project.type === ProjectType.SPECULATIVE_PROJECT) {
                const parentComponent = {
                    where: {
                        project_id: { _eq: project.projectId },
                        hybrid_parent_project_id: { _is_null: true }
                    },
                    _set: { project_name: trimmedDraft }
                };
                const childComponents = project.hybridComponents.map(
                    (child) => ({
                        where: {
                            project_id: { _eq: project.projectId },
                            hybrid_parent_project_id: { _is_null: false },
                            fuel_type: { _eq: child.fuelType }
                        },
                        _set: {
                            project_name: `${trimmedDraft} - ${child.fuelType} component`
                        }
                    })
                );
                await updateSpeculativeProjectName({
                    variables: {
                        updates: [parentComponent, ...childComponents]
                    },
                    refetchQueries: ["getSpeculativeProjects"]
                });
            } else {
                await updateTrackedProjectName({
                    variables: {
                        projectId: project.projectId,
                        subscriptionId: project.subscriptionId,
                        teamId,
                        clusterId: cluster.id,
                        projectName: trimmedDraft === "" ? null : trimmedDraft
                    },
                    refetchQueries: [{ query: GetTrackedProjectsDocument }]
                });
            }
            segmentTrackRenameProject({
                projectId: project.projectId,
                isSpeculative: project.type === ProjectType.SPECULATIVE_PROJECT,
                hadPreviousName: Boolean(project.projectName)
            });
            closeDialog();
        } catch (error) {
            EditableProjectTitleToaster.show({
                intent: Intent.DANGER,
                message:
                    "Encountered an error while renaming the project. Please try again later."
            });
        }
    };

    return (
        <span className={css["EditableProjectTitle"]}>
            {getProjectDisplayName(project)}
            {canEditName && (
                <Button
                    icon={IconNames.EDIT}
                    minimal
                    onClick={openDialog}
                    aria-label="Rename project"
                />
            )}
            <Dialog
                title="Rename Project"
                isOpen={isDialogOpen}
                onClose={closeDialog}
                canOutsideClickClose
            >
                <DialogBody>
                    <FormGroup
                        label="Project name"
                        labelInfo={isSpeculative ? "(required)" : "(optional)"}
                        intent={showNameError ? Intent.DANGER : undefined}
                        helperText={
                            showNameError
                                ? "A project with this name already exists in your portfolio."
                                : isSpeculative
                                ? undefined
                                : "Leave empty to use the queue project ID."
                        }
                    >
                        <InputGroup
                            placeholder={
                                isSpeculative
                                    ? "Insert project name.."
                                    : project.projectId
                            }
                            value={projectName}
                            onValueChange={setProjectName}
                            intent={showNameError ? Intent.DANGER : undefined}
                            autoFocus
                        />
                    </FormGroup>
                </DialogBody>
                <DialogFooter
                    actions={
                        <>
                            <Button text="Cancel" onClick={closeDialog} />
                            <Button
                                intent={Intent.PRIMARY}
                                text="Save"
                                onClick={onSave}
                                disabled={isSaveDisabled}
                                loading={isSaving}
                            />
                        </>
                    }
                />
            </Dialog>
        </span>
    );
};

const EditableProjectTitleToaster = Toaster.create({
    position: Position.TOP
});
